import { toLogicalEventPoint } from '@/utils/screenRotation';
import { onBeforeUnmount, ref } from 'vue';

type HorizontalDragOptions = {
  /** Return false to reject the gesture (not loaded, not permitted, …). */
  onStart?: () => boolean | void;
  /** Travel along the app's horizontal axis, in px, since the gesture began. */
  onMove: (deltaX: number) => void;
  /** Runs after the gesture ends, for cleanup or gesture hand-off. */
  onEnd?: () => void;
  /**
   * Runs on release when the press never crossed `ACTIVATION_TRAVEL_PX` — i.e.
   * it was a tap, not a drag. Fires even if `onStart` went on to reject the
   * drag, since a rejected drag is still a tap as far as the user's finger is
   * concerned.
   */
  onTap?: () => void;
};

/**
 * A press is a tap until it has travelled this far; only then is it a drag.
 * `onStart` is where consumers commit to the gesture — stop playback, latch an
 * anchor, announce it — and a tap has nothing for them to commit to.
 */
const ACTIVATION_TRAVEL_PX = 6;

/**
 * Horizontal press-drag gesture over mouse or touch, tracked on `window` so the
 * pointer can leave the element mid-drag. Listeners are removed on release and
 * on unmount.
 *
 * That axis is not the *screen's* horizontal while `<body>` is rotated for
 * mobile portrait — see `@/utils/screenRotation`.
 *
 * Bind `start` to `@mousedown.prevent` / `@touchstart.prevent`; the modifier is
 * what suppresses text selection and page scrolling during the drag.
 */
export function useHorizontalDrag({ onStart, onMove, onEnd, onTap }: HorizontalDragOptions) {
  const isDragging = ref(false);
  let isPressed = false;
  let startX = 0;
  let moveEvent: 'mousemove' | 'touchmove' = 'mousemove';
  let endEvent: 'mouseup' | 'touchend' = 'mouseup';

  const handleMove = (event: Event) => {
    if (!isPressed) return;
    const x = toLogicalEventPoint(event as MouseEvent | TouchEvent).x;

    if (!isDragging.value) {
      if (Math.abs(x - startX) < ACTIVATION_TRAVEL_PX) return;
      if (onStart?.() === false) {
        // Refused: stay quiet for the rest of the press; `stop` unbinds at release.
        isPressed = false;
        return;
      }
      // Re-anchor where the drag was accepted, so the travel spent crossing the
      // threshold doesn't land as a jump on the first step. That step still runs,
      // at delta 0 — a consumer that reports its position gets to report the one
      // it starts from.
      startX = x;
      isDragging.value = true;
    }

    onMove(x - startX);
  };

  const stop = () => {
    const wasDragging = isDragging.value;
    isPressed = false;
    isDragging.value = false;
    window.removeEventListener(moveEvent, handleMove);
    window.removeEventListener(endEvent, stop);
    if (wasDragging) {
      onEnd?.();
    } else {
      onTap?.();
    }
  };

  const start = (event: MouseEvent | TouchEvent) => {
    if (isPressed) return;

    startX = toLogicalEventPoint(event).x;
    const isTouch = 'touches' in event;
    moveEvent = isTouch ? 'touchmove' : 'mousemove';
    endEvent = isTouch ? 'touchend' : 'mouseup';
    isPressed = true;
    window.addEventListener(moveEvent, handleMove);
    window.addEventListener(endEvent, stop);
  };

  onBeforeUnmount(stop);

  return { isDragging, start, stop };
}
